import { useState, useEffect } from "react";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { useFormik, FormikValues } from "formik";
import * as yup from 'yup';
import { toast } from "sonner";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Button } from "rizzui";
import PageHeader from "../../../layout/shared/page-header";
import CensusGeneralDataForm from "../components/CensusGeneralDataForm";
import CensusLineForm from "../components/CensusLineForm";
import { MdErrorOutline } from "react-icons/md";
import { LuClipboardList } from "react-icons/lu";
import { PiBird } from "react-icons/pi";
import { TbRosetteDiscountCheck } from "react-icons/tb";
import { CensusService } from "../../../services/census/censusService";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useCensusFormContext } from "../components/provider/CensusFormProvider";
import { createLineValidationSchema, generalDataValidationSchema, SuperAdminSchema, TabData } from "../components/types/CensusSchemas";
import { useSelector } from "react-redux";
import { AuthState } from "../../../redux/authSlice";
import { RootState } from "../../../redux/store";
import Swal from "sweetalert2";

const getInitialLineValues = () => ({
  specieId: null,
  turbineId:null,
  windDirection: null,
  windSpeed: null,
  cloudCoverage: null,
  visibility: null,
  numIndividuals: null,
  aeroStatus: 'encendido',
  altitudLevel: null,
  distanceToAero: null,
  riskDetected: null,
  isOfc: 'false',
  dirOrigen: null,
  dirDest: null,
  reacc: 'no',
  aeroOnComments: null,
  askStop: 'no',
  rain: null,
  askAnillado: 'no',
  aeroOn: 'no',
  sex: null,
  age: null,
  flightDirection: null,
  birdReaction: null,
  flightType: null,
  anillado: null,
  photos: [] as File[],
  isProtected: false,
  censusMin: null,
  extraAnillado: null,
});

const CensusCreateTabs = ({ fromPark }: { fromPark?: boolean }) => {

  // PROVIDER

  const { updateWindTurbineHookFilters } = useCensusFormContext();

  // HOOKS
  
  const censusService = new CensusService();
  const { parkId = null } = useParams<{ parkId?: string }>();
  const { userCan } = usePrivilege();
  const navigate = useNavigate();
  const { user }: AuthState = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.roles?.includes('Coordinador') || user?.roles?.includes('Administrador') || user?.roles?.includes('Superadministrador') ? true : false;

  // STATES

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [censusLines, setCensusLines] = useState<Record<string, FormikValues>>({});
  const [tabs, setTabs] = useState<TabData[]>([
    {
      title: "Datos Generales",
      description: "Información básica del censo",
      isValid: true,
      isLine: false,
      formikValues: {
        parkId: fromPark ? parkId : '',
        userId: !isAdmin ? user?.id : '',
        year: '',
        month: '',
        week: '',
        date: '',
        time: '',
        endTime: '',
        censusDpma: '',
        observationPost: '',
      },
      formikErrors: {},
      formikTouched: {}
    }
  ]);

  const breadCrumb = [
    { name: 'Administración' },
    fromPark ? { name: 'Gestión de censos en parque', href: `/parks/${parkId}/profile/census` } : { name: 'Gestión de censos', href: '/census' },
    { name: 'Crear censo' },
  ];

  // FORMIK GENERAL DATA

  const generalDataFormik = useFormik({
    initialValues: tabs[0].formikValues,
    validationSchema: userCan('list', 'companies') ? SuperAdminSchema : generalDataValidationSchema,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
  });

  // FORMIK LINE

  const lineSchema = createLineValidationSchema(
    generalDataFormik.values.time,
    generalDataFormik.values.endTime
  );

  const currentLineFormik = useFormik({
    initialValues: (() => {

      // IF NOT LINE TAB RETURN INITIAL VALUES


      if (activeTabIndex <= 0 || !tabs[activeTabIndex]) {
        return getInitialLineValues();
      }
      
      // GET LINE ID FROM TAB
      
      const currentTab = tabs[activeTabIndex];
      const lineId = currentTab.lineId;
      
      if (lineId && typeof lineId === 'string' && lineId in censusLines) {
        const lineData = censusLines[lineId];
        return {
          ...JSON.parse(JSON.stringify(lineData)),
          photos: lineData.photos || [] // Preserve File objects
        };
      }
      
      const tabData = currentTab.formikValues;
      return {
        ...JSON.parse(JSON.stringify(tabData)),
        photos: tabData.photos || [] // Preserve File objects
      };
    })(),
    validationSchema: lineSchema,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    initialTouched: activeTabIndex > 0 && tabs[activeTabIndex] ? 
      {...tabs[activeTabIndex].formikTouched} : 
      {},
  });

  // FUNCTIONS

  // ---------------------------------------------------------------------------------
  /**
   * @ES MANEJA EL CAMBIO DE PESTAÑA
   * @EN HANDLES TAB CHANGE
   * @param index 
   */
  // ---------------------------------------------------------------------------------
  const handleTabChange = async (index: number) => {
    if (activeTabIndex === 0) {
      setTabs(prev => {
        const updated = [...prev];
        updated[0].formikValues = generalDataFormik.values;
        updated[0].formikTouched = {...generalDataFormik.touched};
        return updated;
      });
    } else if (activeTabIndex > 0 && activeTabIndex < tabs.length) {
      setTabs(prev => {
        const updated = [...prev];
        updated[activeTabIndex].formikValues = currentLineFormik.values;
        updated[activeTabIndex].formikTouched = {...currentLineFormik.touched};
        
        if (updated[activeTabIndex].lineId) {
          setCensusLines(prev => ({
            ...prev,
            [updated[activeTabIndex].lineId!]: currentLineFormik.values
          }));
        }
        return updated;
      });
    }
    
    setActiveTabIndex(index);
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES AÑADE UNA NUEVA PESTAÑA DE LÍNEA
   * @EN ADDS A NEW LINE TAB
   */
  // ---------------------------------------------------------------------------------
  const handleAddLine = async () => {
    const lineId = `line_${tabs.length}`;
    
    const newTabIndex = tabs.length;

    // GET PREV LINE TAB IF EXISTS

    const prevLineTab = tabs[newTabIndex - 1];
    const prevLineValues = prevLineTab ? prevLineTab.formikValues : {};

    const newLineValues = {
      specieId: null,
      turbineId:null,
      windDirection: prevLineValues.windDirection || null,
      windSpeed: prevLineValues.windSpeed || null,
      cloudCoverage: prevLineValues.cloudCoverage || null,
      visibility: prevLineValues.visibility || null,
      numIndividuals: null,
      aeroStatus: 'encendido',
      altitudLevel: null,
      distanceToAero: null,
      riskDetected: false,
      isOfc: 'false',
      dirOrigen: null,
      dirDest: null,
      reacc: 'no',
      aeroOnComments: null,
      askStop: null,
      rain: prevLineValues.rain || null,
      askAnillado: null,
      sex: '0',
      age: '0',
      flightDirection: null,
      birdReaction: null,
      flightType: null,
      anillado: null,
      photos: null as File[] | null,
      isProtected: false,
      censusMin: null,
      time: null,
      extraAnillado: null,
    };
    
    setTabs([
      ...tabs,
      {
        title: `Línea ${newTabIndex}`,
        description: "-",
        isLine: true,
        isValid: true,
        formikValues: newLineValues,
        formikErrors: {},
        formikTouched: {},
        lineId: lineId
      }
    ]);
    
    setCensusLines(prev => ({
      ...prev,
      [lineId]: {...newLineValues}
    }));
    
    setActiveTabIndex(newTabIndex);
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES ELIMINA UNA PESTAÑA DE LÍNEA
   * @EN DELETES A LINE TAB
   * @param index 
   */
  // ---------------------------------------------------------------------------------
  const handleDeleteLine = (index: number) => {
    if (index === 0) {
      toast.error("No puedes eliminar la pestaña de datos generales");
      return;
    }
    
    const lineIdToDelete = tabs[index].lineId;
    
    setTabs(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      
      for (let i = 1; i < updated.length; i++) {
        updated[i].title = `Línea ${i}`;
      }
      
      return updated;
    });
    
    if (lineIdToDelete) {
      setCensusLines(prev => {
        const updated = {...prev};
        delete updated[lineIdToDelete];
        return updated;
      });
    }
    
    if (index === activeTabIndex) {
      setActiveTabIndex(index - 1);
    } else if (index < activeTabIndex) {
      setActiveTabIndex(activeTabIndex - 1);
    }
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES FINALIZA EL CENSO
   * @EN FINISHES THE CENSUS
   */
  // ---------------------------------------------------------------------------------
  const handleFinish = async () => {

    // UPDATE VALUES OF TABS

    if (activeTabIndex === 0) {
      setTabs(prev => {
        const updated = [...prev];
        updated[0].formikValues = generalDataFormik.values;
        return updated;
      });
    } else if (activeTabIndex > 0 && activeTabIndex < tabs.length) {
      setTabs(prev => {
        const updated = [...prev];
        updated[activeTabIndex].formikValues = currentLineFormik.values;
        
        if (updated[activeTabIndex].lineId) {
          setCensusLines(prev => ({
            ...prev,
            [updated[activeTabIndex].lineId!]: currentLineFormik.values
          }));
        }
        return updated;
      });
    }
    
    // VALIDATE ALL TABS

    // FIRST VALIDATE GENERAL DATA

    const generalErrors = await generalDataFormik.validateForm();
    
    const generalTouched = Object.keys(generalDataFormik.values).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    
    generalDataFormik.setTouched(generalTouched);
    
    let hasErrors = Object.keys(generalErrors).length > 0;
    
    // UPDATE STATUS OF GENERAL DATA TAB

    setTabs(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        isValid: Object.keys(generalErrors).length === 0,
        formikErrors: generalErrors,
        formikTouched: generalTouched
      };
      return updated;
    });
    
    // THEN VALIDATE ALL LINE TABS

    for (let i = 1; i < tabs.length; i++) {
      const touchedFields = Object.keys(tabs[i].formikValues).reduce<Record<string, boolean>>((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      
      try {
        await createLineValidationSchema(
          generalDataFormik.values.time,
          generalDataFormik.values.endTime
        ).validate(tabs[i].formikValues, { abortEarly: false });
        
        setTabs(prev => {
          const updated = [...prev];
          updated[i] = {
            ...updated[i],
            isValid: true,
            formikErrors: {},
            formikTouched: touchedFields
          };
          return updated;
        });
        
        // IF THIS IS THE ACTIVE TAB, UPDATE ITS TOUCHED FIELDS

        if (i === activeTabIndex) {
          currentLineFormik.setTouched(touchedFields);
        }

      } catch (validationError) { 
        const errors: Record<string, string> = {};
        if (validationError instanceof yup.ValidationError) {
          validationError.inner.forEach(err => {
            if (err.path) {
              errors[err.path] = err.message;
            }
          });
        }
        
        // UPDATE STATUS OF THIS LINE TAB

        setTabs(prev => {
          const updated = [...prev];
          updated[i] = {
            ...updated[i],
            isValid: false,
            formikErrors: errors,
            formikTouched: touchedFields
          };
          return updated;
        });
        
        // IF THIS IS THE ACTIVE TAB, UPDATE ITS TOUCHED FIELDS

        if (i === activeTabIndex) {
          currentLineFormik.setTouched(touchedFields);
        }
        
        hasErrors = true;
      }
    }
    
    if (hasErrors) {
      toast.error("Hay pestañas con errores. Por favor, revísalas antes de finalizar.");
      return;
    }
    
    // THEN PREPARE CENSUS DATA TO SEND 
    
    const formData = new FormData();
    
    // Add general census data
    if (userCan('list', 'companies') && generalDataFormik.values.company) {
      formData.append('company', generalDataFormik.values.company.toString());
    }
    formData.append('year', generalDataFormik.values.year?.toString() || "");
    formData.append('month', generalDataFormik.values.month?.toString() || "");
    formData.append('week', generalDataFormik.values.week?.toString() || "");
    formData.append('date', generalDataFormik.values.date?.toString() || "");
    formData.append('time', generalDataFormik.values.time?.toString() || "");
    formData.append('endTime', generalDataFormik.values.endTime?.toString() || "");
    formData.append('parkId', generalDataFormik.values.parkId?.toString() || "");
    formData.append('userId', generalDataFormik.values.userId?.toString() || "");
    formData.append('isOfc', "false");
    formData.append('censusDpma', generalDataFormik.values.censusDpma?.toString() || "");
    formData.append('observationPost', generalDataFormik.values.observationPost?.toString() || "");
    
    // Add census lines data
    Object.values(censusLines).forEach((line: any, index: number) => {
      const lineData = {
        specieId: line.specieId?.toString() || "",
        windTurbineId: line.turbineId?.toString() || "",
        windDirection: line.windDirection?.toString() || "",
        windSpeed: line.windSpeed?.toString() || "",
        cloudCoverage: line.cloudCoverage?.toString() || "",
        visibility: line.visibility?.toString() || "",
        numIndividuals: line.numIndividuals?.toString() || "",
        aeroStatus: line.aeroStatus?.toString() || "",
        altitudLevel: line.altitudLevel?.toString() || "",
        distanceToAero: line.distanceToAero?.toString() || "",
        riskDetected: line.riskDetected?.toString() || "",
        isOfc: 'false',
        dirOrigen: line.dirOrigen?.toString() || "",
        dirDest: line.dirDest?.toString() || "",
        reacc: line.reacc?.toString() || "",
        aeroOnComments: line.aeroOnComments?.toString() || "",
        askStop: line.askStop?.toString() || "",
        rain: line.rain?.toString() || "",
        askAnillado: line.askAnillado?.toString() || "",
        sex: line.sex?.toString() || "",
        age: line.age?.toString() || "",
        flightDirection: line.flightDirection?.toString() || "",
        birdReaction: line.birdReaction?.toString() || "",
        flightType: line.flightType?.toString() || "",
        anillado: line.anillado?.toString() || "",
        isProtected: line.isProtected?.toString() || "",
        censusMin: line.censusMin?.toString() || "",
        time: line.time?.toString() || "",
        extraAnillado: line.extraAnillado?.toString() || "",
      };
      
      // Add each field of the census line to FormData
      Object.entries(lineData).forEach(([key, value]) => {
        formData.append(`censusLines[${index}][${key}]`, value);
      });
      
      // Add line photos if they exist
      if (line.photos && Array.isArray(line.photos)) {
        line.photos.forEach((photo: File, photoIndex: number) => {
          formData.append(`censusLines_${index}_photos_${photoIndex}`, photo);
        });
      }
    });
    
    try {
      const response = await censusService.createCensus(formData);
      const result = await response.getResponseData();
      
      if (result.success) {
        toast.success("Censo creado correctamente");
        if (fromPark) {
          navigate(`/parks/${parkId}/profile/census`);
        } else {
          navigate("/census");
        }
      } else {
        toast.error("Error al crear el censo: " + result.message);
      }
    } catch (error) {
      console.error("Error al crear el censo:", error);
      toast.error("Error al crear el censo");
    }
  };
  // ---------------------------------------------------------------------------------

  // USE EFFECT
  
  useEffect(() => {
    if (parkId != null) {
      updateWindTurbineHookFilters({
        filter_filters: {
          park: parkId,
        },
      });
    } else if (generalDataFormik.values.parkId != null) {
      updateWindTurbineHookFilters({
        filter_filters: {
          park: generalDataFormik.values.parkId,
        },
      });
    }
  }, [parkId, generalDataFormik.values.parkId]);
  
  // RENDER

  return (
    <>
      <PageHeader title="Crear Censo" breadcrumb={breadCrumb} />
      
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <TabGroup selectedIndex={activeTabIndex} onChange={handleTabChange}>
          <div className="flex justify-between items-center mb-6">
            <TabList className="flex space-x-1 overflow-x-auto p-1 bg-gray-100 rounded-lg">
              {tabs.map((tab, index) => (
                <Tab
                  key={index + `${tab.isValid}`}
                  className={({ selected }) =>
                    `py-2.5 px-4 text-sm font-medium rounded-md whitespace-nowrap
                    ${selected
                      ? 'text-primary shadow bg-white'
                      : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'}
                    ${!tab.isValid ? 'border-2 border-red-500 text-red' : ''}
                    `
                  }
                >
                  <div className="flex items-center">
                    { tab.isLine ? <LuClipboardList /> : <PiBird />}
                    <span className="ms-2 me-2">{tab.title}</span>
                    {!tab.isValid ? <MdErrorOutline className="text-red"/> : <TbRosetteDiscountCheck className="text-success" />}
                    {index > 0 && (
                      <button
                        type="button"
                        className="ml-2 text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLine(index);
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                </Tab>
              ))}
              
              <button
                type="button"
                className="py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-md whitespace-nowrap"
                onClick={handleAddLine}
              >
                <div className="flex items-center">
                  <FiPlus size={16} className="mr-1" />
                  <span>Añadir línea</span>
                </div>
              </button>
            </TabList>
          </div>
          
          <TabPanels className="mt-4">

            {/* GENERAL DATA */}

            <TabPanel>
              <CensusGeneralDataForm 
                formik={generalDataFormik}
                parkId={parkId}
                onValidationChange={(isValid) => {
                  setTabs(prev => {
                    const updated = [...prev];
                    updated[0].isValid = isValid;
                    return updated;
                  });
                }}
              />
            </TabPanel>
            
            {/* CENSUS LINES */}

            {tabs.slice(1).map((tab, index) => (
              <TabPanel key={index + 1}>
                <CensusLineForm 
                  key={tab.lineId}
                  initialValues={tab.formikValues}
                  lineNumber={index + 1}
                  lineId={tab.lineId}
                  parkId={generalDataFormik.values.parkId}
                  censusStartTime={generalDataFormik.values.time}
                  censusEndTime={generalDataFormik.values.endTime}
                  initialTouched={tab.formikTouched}
                  onValidationChange={(isValid) => {
                    setTabs(prev => {
                      const updated = [...prev];
                      updated[index + 1].isValid = isValid;
                      return updated;
                    });
                  }}
                  onFormChange={(values) => {
                    setTabs(prev => {
                      const updated = [...prev];
                      updated[index + 1].formikValues = values;
                      return updated;
                    });
                    
                    if (tab.lineId) {
                      setCensusLines(prev => ({
                        ...prev,
                        [tab.lineId!]: values
                      }));
                    }
                    
                    if (index + 1 === activeTabIndex) {
                      currentLineFormik.setValues(values);
                    }
                  }}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
        
        <div className="flex justify-end mt-8 space-x-4">

          {/* CANCEL BUTTON */}
          
          <Button variant="outline" onClick={() => {
            if (generalDataFormik.dirty) {
                Swal.fire({
                  title: 'Confirmar cancelación',
                  text: '¿Estás seguro de que deseas cancelar? Se perderán los cambios no guardados.',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: 'red',
                  cancelButtonColor: '#36882D',
                  confirmButtonText: 'Sí, cancelar',
                  cancelButtonText: 'No, mantener cambios'
                }).then((result) => {
                  if (result.isConfirmed) {
                    if (fromPark) {
                      navigate(`/parks/${parkId}/profile/census`);
                    } else {
                      navigate('/census');
                    }
                  }
                });
                return;
              } else {
                if (fromPark) {
                  navigate(`/parks/${parkId}/profile/census`);
                } else {
                  navigate('/census');
                }
              }
          }}>
            Cancelar
          </Button>

          {/* FINISH BUTTON */}
          
          <Button variant="solid" onClick={handleFinish}>
            Finalizar
          </Button>

        </div>
      </div>
    </>
  );
};

export default CensusCreateTabs;